'use client';

import React, { useEffect, useState } from 'react';

import { Button, Typography } from 'lighthouse';
import { MinusNoneOutlineMdSVG, PlusNoneOutlineMdSVG } from 'lighthouse-icons';

import classes from './index.module.css';
import { CounterProps } from './models';

import { Trash } from 'iconsax-react';

const Counter = ({ min = 1, max = Infinity, step = 1, value: controlledValue, defaultValue = 1, onChange, disabled, isLoading }: CounterProps) => {
    const [internalValue, setInternalValue] = useState<number>(defaultValue);

    const isControlled = controlledValue !== undefined;
    const currentValue = isControlled ? controlledValue : internalValue;

    const clampValue = (newValue: number) => {
        return Math.min(Math.max(newValue, min), max);
    };

    const increment = () => {
        const newValue = clampValue(currentValue + step);
        if (!isControlled) {
            setInternalValue(newValue);
        }
        onChange?.(newValue);
    };

    const decrement = () => {
        const newValue = clampValue(currentValue - step);

        if (!isControlled) {
            setInternalValue(newValue);
        }
        onChange?.(newValue);
    };

    const removeItem = () => {
        onChange?.(0);
    };

    useEffect(() => {
        if (isControlled) {
            setInternalValue(clampValue(controlledValue));
        }
    }, [controlledValue, isControlled]);

    const isMinValue = currentValue <= min;
    const isMaxValue = currentValue >= max;
    const isActionDisabled = !!disabled || !!isLoading;

    return (
        <div className='flex items-center gap-2'>
            <Button
                onClick={increment}
                disabled={isMaxValue || isActionDisabled}
                loading={isLoading}
                variant='contained'
                size='small'
                className={`flex h-7 w-10 items-center justify-center ${classes.counterButton}`}>
                {!isLoading && <PlusNoneOutlineMdSVG />}
            </Button>

            <div className='flex min-w-6 justify-center'>
                <Typography className={`text-center font-bold ${isLoading ? 'opacity-60' : ''}`} color='primary'>
                    {currentValue}
                </Typography>
            </div>

            {isMinValue ? (
                <Button
                    onClick={removeItem}
                    disabled={isActionDisabled}
                    loading={isLoading}
                    variant='outlined'
                    size='small'
                    color='error'
                    className={`flex h-7 w-10 items-center justify-center ${classes.counterButtonMin}`}>
                    {!isLoading && <Trash color='currentcolor' className={'size-4 text-error'} />}
                </Button>
            ) : (
                <Button
                    onClick={decrement}
                    variant='outlined'
                    disabled={isActionDisabled}
                    loading={isLoading}
                    size='small'
                    color='primary'
                    className={`flex h-7 w-10 items-center justify-center ${classes.counterButton}`}>
                    {!isLoading && <MinusNoneOutlineMdSVG />}
                </Button>
            )}
        </div>
    );
};

export default Counter;
